import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { Icon } from '../components/icons';
import { InvoicePill, useHasOpenInvoices } from '../features/billing/invoices';
import { PlanBadge } from '../features/billing/PlanBadge';
import { shortcutLabel, useCommandPalette } from './CommandPalette';
import { NAV_FOOTER, NAV_GROUPS, isNavActive, type NavEntry } from './nav';
import { useSession } from './session';
import { AccountMenu, WorkspaceSwitcher, initialsOf, useUnreadCount } from './ShellControls';

/**
 * Desktop navigation (refonte): workspace switcher, ⌘K search, the grouped
 * areas, then Channels and Settings in the footer above the signed-in person.
 * Active state comes from `isNavActive`, not NavLink's prefix match, so
 * Settings is not lit on /settings/channels.
 */

/** 36px rows; 44px on a touch screen (a tablet shows the sidebar from 768px). */
const ITEM =
  'flex h-9 items-center gap-2.5 rounded-[9px] px-2.5 text-[13.5px] transition-colors hover:bg-white/8 hover:text-white [@media(pointer:coarse)]:h-11';

function NavItem({ entry, badge, invoice }: { entry: NavEntry; badge?: number; invoice?: boolean }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const active = isNavActive(entry, pathname);
  return (
    <Link
      to={entry.to}
      aria-current={active ? 'page' : undefined}
      className={`${ITEM} ${active ? 'bg-white/12 font-semibold text-white' : 'font-medium text-(--color-sidebar-text)'}`}
    >
      <Icon name={entry.icon} className="size-4 shrink-0" />
      <span className="flex-1 truncate">{t(`nav.${entry.label}`)}</span>
      {badge ? (
        <span className="flex h-[18px] min-w-5 items-center justify-center rounded-full bg-(--color-highlight) px-1.5 text-[11px] font-bold text-white">
          {badge}
          <span className="sr-only"> {t('nav.unread', { count: badge })}</span>
        </span>
      ) : null}
      {invoice ? <InvoicePill onDark /> : null}
      {entry.feature ? <PlanBadge feature={entry.feature} onDark /> : null}
    </Link>
  );
}

function SearchField() {
  const { t } = useTranslation();
  const palette = useCommandPalette();
  return (
    <button
      type="button"
      onClick={palette.open}
      aria-keyshortcuts="Meta+K Control+K"
      className="flex h-9 shrink-0 items-center gap-2 rounded-[10px] [@media(pointer:coarse)]:h-11 border border-white/8 bg-white/5 px-2.5 text-left text-[13px] text-(--color-sidebar-muted) transition-colors hover:bg-white/8 hover:text-white"
    >
      <Icon name="search" className="size-[15px] shrink-0" />
      <span className="flex-1">{t('nav.search')}</span>
      <kbd className="rounded-[5px] bg-white/8 px-1.5 py-0.5 font-sans text-[11px] text-(--color-sidebar-text)">{shortcutLabel()}</kbd>
    </button>
  );
}

export function Sidebar() {
  const { t } = useTranslation();
  const { session } = useSession();
  const unread = useUnreadCount();
  const invoiceDue = useHasOpenInvoices();

  return (
    /* The workspace and search stay on top and the account at the bottom; only
       the links between them scroll, so a short window never hides the account
       menu (and Log out) below the fold. On a tall one the groups' flex-1 keeps
       Channels and Settings just above the account. */
    <div className="flex h-full flex-col gap-3.5 pb-3.5 pt-[18px]">
      <div className="flex shrink-0 flex-col gap-3.5 px-3.5">
        <WorkspaceSwitcher />
        <SearchField />
      </div>

      <div className="scrollbar-on-dark flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto px-3.5">
        <div className="flex-1">
          <div className="flex flex-col gap-3.5">
            {NAV_GROUPS.map((group, index) => (
              <div key={group.label ?? index} role="group" aria-label={group.label ? t(`nav.${group.label}`) : undefined} className="flex flex-col gap-0.5">
                {group.label ? (
                  <p aria-hidden className="px-2.5 pb-1.5 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-(--color-sidebar-label)">
                    {t(`nav.${group.label}`)}
                  </p>
                ) : null}
                {group.items.map((entry) => (
                  <NavItem key={entry.to} entry={entry} badge={entry.to === '/inbox' ? unread : undefined} />
                ))}
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-0.5">
          {NAV_FOOTER.map((entry) => (
            <NavItem key={entry.to} entry={entry} invoice={entry.to === '/settings' && invoiceDue} />
          ))}
        </div>
      </div>

      <div className="mx-3.5 flex shrink-0 items-center gap-2.5 border-t border-white/8 pl-1.5 pr-1 pt-3">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-[image:var(--gradient-avatar)] text-[13px] font-bold text-white">
          {initialsOf(session?.user.firstName, session?.user.email)}
        </span>
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-[13px] font-semibold text-white">{session?.user.firstName}</span>
          <span className="truncate text-[11.5px] text-(--color-sidebar-muted)">{session?.user.email}</span>
        </div>
        <AccountMenu variant="sidebar" />
      </div>
    </div>
  );
}
